'use client';
import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, LazyMotion } from 'framer-motion';
import { variantsFadeInUp } from '../__constants__/animation-variants';
import { useInView } from 'react-cool-inview';

const loadFeatures = () => import('../__constants__/framer-features').then((res) => res.default);

type AnimationFadeLazyProps = {
    children: JSX.Element | JSX.Element[];
    className?: string;
    threshold?: number;
    fadeOut?: boolean;
};

// Improved version for React 19 compatibility
export const AnimationFadeLazy = (props: AnimationFadeLazyProps) => {
    const { children, className, threshold = 0.2, fadeOut = false, ...rest } = props;
    const controls = useAnimation();

    // Use ref for timeout to avoid setState in effect
    const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Track mount state to prevent controls.start() before mount
    const hasMountedRef = useRef(false);

    useEffect(() => {
        hasMountedRef.current = true;

        // Set a safety timeout to make elements visible after 2 seconds
        // This ensures components appear even if intersection observer fails
        safetyTimeoutRef.current = setTimeout(() => {
            controls.start('visible');
        }, 2000);

        return () => {
            // Clear the timeout on cleanup
            if (safetyTimeoutRef.current) {
                clearTimeout(safetyTimeoutRef.current);
            }
        };
    }, [controls]);

    const { observe } = useInView({
        threshold,
        onEnter: ({ entry }) => {
            if (!hasMountedRef.current) return;
            if (entry.isIntersecting) {
                // Clear safety timeout since we're visible now
                if (safetyTimeoutRef.current) {
                    clearTimeout(safetyTimeoutRef.current);
                }
            }
            controls.start('visible');
        },
        onLeave: () => {
            if (!hasMountedRef.current) return;
            if (fadeOut) {
                controls.start('hidden');
            }
        },
    });

    return (
        <LazyMotion features={loadFeatures}>
            <motion.div
                className={`relative ${className || ''}`}
                ref={observe}
                animate={controls}
                initial="hidden"
                variants={variantsFadeInUp}
                {...rest}
            >
                {children}
            </motion.div>
        </LazyMotion>
    );
};

export default AnimationFadeLazy;
